<template>
  <transition name="modal">
    <div class="modal-mask" @click.self="backClose" :class="{ overflow, gray, 'modal-top': top || overflow, 'move-enabled': moveEnabled }">
      <div class="modal-container" :class="this.contentClass" @mousedown="moveStart" @mouseup="moveEnd" ref="container">
        <div class="modal-close" @click="close" title="Close" v-if="showCloseIcon"><i class="far fa-times"></i></div>
        <slot></slot>
      </div>
    </div>
  </transition>
</template>

<script>
import { mapMutations } from 'vuex'
import { MODAL_TOGGLE_STATE } from '@/store'

/**
 * Display any content in modal
 * Options:
 * - overflow (bool) - enable overflow support for content
 * - content-class (string) - class for modal content container
 * - gray (bool) - enable gray backgroud
 * - closeable (bool) - disable close on background click
 * - show-close-icon (bool) - display close icon or no
 * - top (bool) - if true content will be aligned with 10% margin from top, if false content will be aligned by center
 */
export default {
  name: 'app-modal',
  props: {
    overflow: Boolean,
    contentClass: String,
    gray: Boolean,
    onBackClose: {
      type: Boolean,
      default: true
    },
    closeable: {
      type: Boolean,
      default: true
    },
    showCloseIcon: {
      type: Boolean,
      default: false
    },
    top: Boolean
  },
  data: () => ({
    moveTimeoutId: 0,
    moveEnabled: false,
    shiftX: 0,
    shiftY: 0
  }),
  methods: {
    ...mapMutations({
      toggleModal: MODAL_TOGGLE_STATE
    }),
    backClose() {
      if (this.onBackClose) {
        this.close()
      }
    },
    close() {
      if (this.closeable) {
        this.$emit('close')
      }
    },
    move(e) {
      if (this.moveEnabled) {
        this.$refs.container.style.left = e.pageX - this.shiftX + 'px'
        this.$refs.container.style.top = e.pageY - this.shiftY + 'px'
      } else {
        throw new Error('Move handler fired but `moveEnabled` option is false!')
      }
    },
    moveStart(e) {
      clearTimeout(this.moveTimeoutId)
      this.moveTimeoutId = setTimeout(() => {
        this.moveEnabled = true
        this.shiftX = e.clientX - this.$refs.container.getBoundingClientRect().left
        this.shiftY = e.clientY - this.$refs.container.getBoundingClientRect().top
        this.move(e)
        document.addEventListener('mousemove', this.move)
      }, 500)

      // prevent movement on mouse move
      document.addEventListener('mousemove', (e) => {
          clearTimeout(this.moveTimeoutId)
        }, { once: true })
    },
    moveEnd() {
      clearTimeout(this.moveTimeoutId)
      this.moveEnabled = false
      document.removeEventListener('mousemove', this.move)
    }
  },
  mounted() {
    this.toggleModal(true)
  },
  destroyed() {
    document.removeEventListener('mousemove', this.move)
    this.toggleModal(false)
  }
}
</script>

<style scoped lang="scss">
.modal-mask {
  position: fixed;
  z-index: 9998;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(0, 0, 0, 0.5);
  &.overflow {
    overflow: auto;
    overflow-y: scroll;
  }

  &.gray {
    background-color: #ccc !important;
  }

  &.modal-top {
    align-items: flex-start;

    & .modal-container {
      top: 100px;
    }
  }

  &.move-enabled .modal-container {
    box-shadow: -1px -1px 10px rgba(0, 0, 0, 0.8);
  }
}

.modal-container {
  background-color: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.33);
  position: absolute;
}

.modal-header h3 {
  margin-top: 0;
  color: #42b983;
}

.modal-body {
  margin: 20px 0;
}

.modal-close {
  font-size: 30px;
  line-height: 30px;
  color: rgba(0, 0, 0, 0.7);
  position: absolute;
  right: 0;
  top: 0;
  padding: 14px 14px 0 0;
  cursor: pointer;
  z-index: 1;

  &:hover {
    color: #000;
  }
}
</style>
